// Requester-facing outbox: every tenant request I have created. Includes
// a "new request" form so landlords/agents can start a KYC flow without
// jumping to a contract or property page first.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { formatDate } from '../core/format.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';

const STATUS_ORDER = ['DRAFT', 'SENT', 'IN_REVIEW', 'APPROVED', 'CANCELLED'];

export class TenantRequestsOutboxView extends Component {
  constructor() {
    super();
    this.state = { rows: null, error: null, addOpen: false, busy: false };
  }

  template() {
    const rows = this.state.rows;
    return `
      <section class="stack">
        <div class="row-between">
          <h2>${esc(t('tenant_requests.outgoing.title'))}</h2>
          <div class="row" style="gap:var(--space-2)">
            <a href="#/tenant-requests/incoming" class="btn btn-ghost">
              ${icon('inbox', { size: 'sm' })}<span>${esc(t('tenant_requests.inbox.link'))}</span>
            </a>
            <button class="btn btn-primary" data-action="new">
              ${icon('plus', { size: 'sm' })}<span>${esc(t('tenant_requests.new'))}</span>
            </button>
          </div>
        </div>
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${this.state.addOpen ? this._formHtml() : ''}
        ${rows === null
          ? `<div class="muted">${esc(t('common.loading'))}</div>`
          : rows.length === 0
            ? `<div class="empty">${esc(t('tenant_requests.outgoing.empty'))}</div>`
            : this._listHtml(rows)}
      </section>
    `;
  }

  _listHtml(rows) {
    const sorted = [...rows].sort((a, b) => {
      const ai = STATUS_ORDER.indexOf(a.status); const bi = STATUS_ORDER.indexOf(b.status);
      if (ai !== bi) return ai - bi;
      return String(b.created_at || '').localeCompare(String(a.created_at || ''));
    });
    return `<div class="list">${sorted.map((r) => this._rowHtml(r)).join('')}</div>`;
  }

  _rowHtml(r) {
    const due = r.due_at ? formatDate(String(r.due_at).slice(0, 10)) : null;
    const tenant = r.tenant_name || r.tenant_email || '';
    return `
      <a class="list-item" href="#/tenant-requests/${esc(r.id)}">
        ${icon('send', { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(r.title)}</strong>
          <span class="faint">${esc(t('tenant_requests.row.to', { who: tenant }))}${due ? ' · ' + esc(t('tenant_requests.row.due', { date: due })) : ''}</span>
        </div>
        <span class="badge">${esc(r.status)}</span>
        ${icon('chevron-right', { class: 'icon-faint' })}
      </a>
    `;
  }

  _formHtml() {
    return `
      <form class="card stack" data-new-form>
        <h3>${esc(t('tenant_requests.form.title'))}</h3>
        <div class="field">
          <label>${esc(t('tenant_requests.form.tenant_email'))}</label>
          <input class="input" name="tenant_email" type="email" required placeholder="tenant@example.com" />
        </div>
        <div class="field">
          <label>${esc(t('tenant_requests.form.subject'))}</label>
          <input class="input" name="title" required maxlength="190" placeholder="${esc(t('tenant_requests.form.subject_placeholder'))}" />
        </div>
        <div class="field">
          <label>${esc(t('tenant_requests.form.note'))}</label>
          <textarea class="input" name="note" rows="2" placeholder="${esc(t('tenant_requests.form.note_placeholder'))}"></textarea>
        </div>
        <div class="row" style="justify-content:flex-end; gap:var(--space-3)">
          <button type="button" class="btn btn-ghost" data-action="cancel-new">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary" ${this.state.busy ? 'disabled' : ''}>
            ${esc(t('tenant_requests.form.submit'))}
          </button>
        </div>
      </form>
    `;
  }

  mount() {
    this.on('[data-action="new"]', 'click', () => this.setState({ addOpen: !this.state.addOpen, error: null }));
    this.on('[data-action="cancel-new"]', 'click', () => this.setState({ addOpen: false, error: null }));
    const form = this.el.querySelector('[data-new-form]');
    if (form) form.addEventListener('submit', (e) => this._create(e));
    if (this.state.rows === null) this._load();
  }

  async _load() {
    try {
      const r = await api.get('/tenant-requests/outgoing');
      this.setState({ rows: r.data || [] });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenant_requests.errors.load') });
    }
  }

  async _create(e) {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      tenant_email: String(fd.get('tenant_email') || '').trim(),
      title: String(fd.get('title') || '').trim(),
      note: String(fd.get('note') || '').trim() || null,
    };
    this.setState({ busy: true });
    try {
      const created = await api.post('/tenant-requests', payload);
      window.location.hash = `#/tenant-requests/${created.id}`;
    } catch (err) {
      this.setState({ busy: false, error: err instanceof ApiError ? err.message : t('tenant_requests.errors.create') });
    }
  }
}
